import { lazy, Suspense } from "react";
import type { CollectionWagsListing } from "@/features/listings";
import { PhotoLoadingIndicator } from "@/features/listings";
import type { useUserLocation } from "@/features/viewer-location";
import type { DogPageMode } from "./dog-listing-presentation";
import { useAfterIdle } from "./use-after-idle";

type DogListing = CollectionWagsListing;

const SimilarListings = lazy(() =>
    import("./similar-listings").then((module) => ({
        default: module.SimilarListings,
    })),
);
const ImageCarouselDialog = lazy(() =>
    import("@wags/ui/image-carousel-dialog.tsx").then((module) => ({
        default: module.ImageCarouselDialog,
    })),
);

export function ListingRecommendations({
    mode,
    dog,
    userLocation,
    className,
}: {
    mode: DogPageMode;
    dog: DogListing;
    userLocation: ReturnType<typeof useUserLocation>["location"];
    className: string;
}) {
    const showRail = useAfterIdle();
    if (mode === "draft" || !showRail) return null;
    return (
        <Suspense fallback={null}>
            <SimilarListings
                key={dog.id}
                dog={dog}
                userLocation={
                    userLocation
                        ? { lat: userLocation.lat, lon: userLocation.lon }
                        : null
                }
                className={className}
            />
        </Suspense>
    );
}

export function ListingCarousel({
    mounted,
    mediaItems,
    open,
    onOpenChange,
    alt,
    index,
    onIndexChange,
}: {
    mounted: boolean;
    mediaItems: { url: string; mimeType?: string | null }[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    alt: string;
    index: number;
    onIndexChange: (index: number) => void;
}) {
    if (!mounted || mediaItems.length === 0) return null;
    return (
        <Suspense fallback={null}>
            <ImageCarouselDialog
                mediaItems={mediaItems}
                open={open}
                onOpenChange={onOpenChange}
                alt={alt}
                initialIndex={index}
                onIndexChange={onIndexChange}
                listingMedia
                loadingPlaceholder={<PhotoLoadingIndicator />}
            />
        </Suspense>
    );
}
